<template>
  <template
    v-for="(heroSection, heroIndex) in item.fields.heroSections"
    :key="heroSection?.sys?.id ?? heroIndex"
  >
    <!-- First hero: sync MappedComponent so LCP chunk is not deferred -->
    <MappedComponent
      v-if="heroSection && heroIndex === firstHeroIndex"
      :binding="{
        'toc-info': tocInfo
      }"
      :item="heroSection"
      v-bind="
        ContentfulLivePreview.getProps({
          entryId: heroSection.sys.id,
          fieldId: 'internalName'
        })
      "
    />
    <LazyMappedComponent
      v-else-if="heroSection"
      :binding="{
        'toc-info': tocInfo
      }"
      :item="heroSection"
      v-bind="
        ContentfulLivePreview.getProps({
          entryId: heroSection.sys.id,
          fieldId: 'internalName'
        })
      "
    />
  </template>

  <DocumentCenterSearchbar v-if="isDownloadCenter" />
  <SoftwareCenterSearchbar v-if="isSoftwareCenter" />
</template>

<script setup lang="ts">
import { isEntryOfContentType } from '~/utils/guards/isEntryOfContentType';
import { pageToDefaultThemeMap } from '~/utils/pageToDefaultThemeMap';
import { ContentfulLivePreview } from '@contentful/live-preview';
import { INJECTED_COLOR_MODE } from '~~/lib/constants';
import type { TOCInfo } from '~/types/TocType';
import type { IContentPage } from '~~/lib/ContentfulService/generated/contentful';

const SoftwareCenterSearchbar = defineAsyncComponent(
  () => import('~/components/downloads/software/SoftwareCenterSearchbar.vue')
);
const DocumentCenterSearchbar = defineAsyncComponent(
  () => import('~/components/downloads/documents/DocumentCenterSearchbar.vue')
);

interface Props {
  item: IContentPage;
  tocInfo?: TOCInfo;
}

const props = defineProps<Props>();

// DBP-4325: first resolvable hero is above-the-fold — do not code-split it
const firstHeroIndex = computed(
  () => props.item.fields.heroSections?.findIndex((section) => !!section) ?? -1
);

const pageTheme = computed(
  () =>
    props.item.fields.pageTheme ??
    pageToDefaultThemeMap[props.item.sys.contentType.sys.id]
);

// XXX: there might be a more elegant solution to this.
// @see https://gcp.baslerweb.com/jira/browse/DBP-930
const isDownloadCenter = computed(() =>
  props.item?.fields?.contentSection?.some((item) => {
    if (!isEntryOfContentType(item, 'technicalComponent')) {
      return false;
    }

    return item.fields?.type === 'documentCenter';
  })
);

const isSoftwareCenter = computed(() =>
  props.item?.fields?.contentSection?.some((item) => {
    if (!isEntryOfContentType(item, 'technicalComponent')) {
      return false;
    }

    return item.fields?.type === 'softwareCenter';
  })
);

watch(
  () => pageTheme.value,
  () => {
    provide(INJECTED_COLOR_MODE, readonly(pageTheme));
  },
  { immediate: true }
);
</script>
